import type { TFunction } from 'i18next'
import { ApiError } from './api'
import type { PortalAccessGrant, PortalRole } from './types'

/** Settings › Access list order and search (MS-997 D5) — the prototype's
 * `renderList()` (subscription-portal.html, Settings › Access script): sort
 * by Email (case-insensitive `localeCompare`) or by Added (date), either way
 * flipped by Order; search keeps the rows whose email contains the trimmed,
 * lower-cased query. The default is Email, Ascending. */
export type GrantSortKey = 'email' | 'added'
export type GrantSortOrder = 'asc' | 'desc'

export const DEFAULT_GRANT_SORT: GrantSortKey = 'email'
export const DEFAULT_GRANT_ORDER: GrantSortOrder = 'asc'

/** The prototype's `added(tr)`: a date that does not parse sorts as 0. */
function addedAt(grant: PortalAccessGrant): number {
  const at = grant.grantedAt ? Date.parse(grant.grantedAt) : Number.NaN
  return Number.isNaN(at) ? 0 : at
}

export function sortAndFilterGrants(
  rows: readonly PortalAccessGrant[],
  query: string,
  by: GrantSortKey,
  order: GrantSortOrder
): PortalAccessGrant[] {
  const q = query.trim().toLowerCase()
  const dir = order === 'desc' ? -1 : 1
  const compare =
    by === 'added'
      ? (a: PortalAccessGrant, b: PortalAccessGrant) => (addedAt(a) - addedAt(b)) * dir
      : (a: PortalAccessGrant, b: PortalAccessGrant) =>
          a.email.toLowerCase().localeCompare(b.email.toLowerCase()) * dir
  return rows.filter((row) => !q || row.email.toLowerCase().includes(q)).sort(compare)
}

/** The email already holds a grant here — checked against every row, search
 * or not (the prototype reads every `.mcp-uemail`, hidden ones included). */
export function hasGrant(rows: readonly PortalAccessGrant[], email: string): boolean {
  return rows.some((row) => row.email.toLowerCase() === email)
}

/** The Add User modal's contents (MS-997 D6): what was typed, and an inline
 * message to open with (a server refusal of this draft). */
export interface AddPortalUserDraft {
  email: string
  role: PortalRole
  error: string | null
}

/** "+ Add user": an empty email and Controller (prototype L4037-4042). */
export const EMPTY_ADD_DRAFT: AddPortalUserDraft = { email: '', role: 'controller', error: null }

/** What a refused grant shows in the reopened Add modal (MS-997 D6; CF §5.4),
 * or null when it is not the form's to show: a 403 is handled globally (a
 * demoted caller is sent Home, a revoked one to Access denied). */
export function grantErrorMessage(err: unknown, email: string, t: TFunction): string | null {
  if (!(err instanceof ApiError)) return t('settings.access.directoryUnavailable')
  if (err.status === 403) return null
  switch (err.code) {
    case 'portal_access.already_granted':
      return t('settings.access.addForm.emailExists', { email })
    case 'portal_access.directory_conflict':
      return t('settings.access.directoryConflict')
    case 'validation.email_required':
      return t('settings.access.addForm.emailRequired')
    case 'validation.email_invalid':
      return t('settings.access.addForm.emailInvalid')
    default:
      // 502 directory_unavailable, 429 invite_rate_limited, anything else (Q28).
      return t('settings.access.directoryUnavailable')
  }
}

/** What a refused role change or revoke shows under the table, or null (a
 * 403 is handled globally, a 404 just refetches the list). */
export function rowWriteErrorMessage(err: unknown, t: TFunction): string | null {
  if (err instanceof ApiError) {
    if (err.status === 403 || err.status === 404) return null
    if (err.code === 'portal_access.last_admin') return t('settings.access.lastAdmin')
  }
  return t('common.saveError')
}
